import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { APIService } from "src/app/api/api.service";
import { environment } from "src/environments/environment";
import { NotifierService } from "src/app/_services/notifier.service";
import { Subscription } from "rxjs";

@Component({
  selector: "app-eks-dashboard",
  templateUrl: "./eks-dashboard.component.html",
  styleUrls: ["../eks-requests.styles.css"],
})
export class EksDashboardComponent implements OnInit, OnDestroy {
  @Input() preserveServiceCatalogHeader = false;

  private role: string = (localStorage.getItem("role") || "").toLowerCase();
  urlPrefix: string = this.role == "admin" ? "admin" : "client";
  apiBaseUrl: string = environment.apiURL || APIService.API_ENDPOINTV3;

  loading = false;
  error = "";
  rows: any[] = [];
  accountId = "";
  region = "";
  currentMessage: Subscription;

  nextKey = "";
  hasMore = false;
  readonly pageSize = 20;
  statusLoadingByRequestId: { [key: string]: boolean } = {};
  clusterRuntimeStatusByRequestId: { [key: string]: string } = {};
  private loadSequence = 0;
  private contextRefreshTimer: any = null;
  private destroyed = false;

  showRequestEksModal = false;

  constructor(private apiServer: APIService, private notifier: NotifierService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.accountId = this.normalize(localStorage.getItem("accountId"));
    this.region = this.normalize(localStorage.getItem("regionId"));
    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      if (!msg) return;
      try {
        const d = JSON.parse(msg);
        if (d.value == null || d.source != "notifier") return;
        if (d.key == "accountId" || d.key == "regionId") {
          this.applyContextChange(d.key, d.value);
        }
      } catch {}
    });
    this.loadRequests(true);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.currentMessage) this.currentMessage.unsubscribe();
    if (this.contextRefreshTimer) clearTimeout(this.contextRefreshTimer);
  }
  private normalize(v: any): string { const s = (v ?? "").toString().trim(); return !s || s === "null" || s === "undefined" ? "" : s; }
  private headers() { return { Authorization: localStorage.getItem("t") }; }
  private async get(url: string) {
    try {
      const r = await this.apiServer.getDataPromis(url, {}, this.headers());
      return typeof r == "string" ? null : r;
    } catch {
      return null;
    }
  }

  private refreshView() {
    if (!this.destroyed) this.cdr.detectChanges();
  }

  private applyContextChange(key: string, value: any) {
    const normalizedValue = this.normalize(value);
    if (key === "accountId" && this.accountId === normalizedValue) return;
    if (key === "regionId" && this.region === normalizedValue) return;

    this.accountId = key === "accountId" ? normalizedValue : this.normalize(localStorage.getItem("accountId"));
    this.region = key === "regionId" ? normalizedValue : this.normalize(localStorage.getItem("regionId"));
    this.rows = [];
    this.nextKey = "";
    this.hasMore = false;
    this.error = "";
    this.statusLoadingByRequestId = {};
    this.clusterRuntimeStatusByRequestId = {};
    this.loading = true;
    this.scheduleContextRefresh();
    this.refreshView();
  }

  private scheduleContextRefresh() {
    if (this.contextRefreshTimer) clearTimeout(this.contextRefreshTimer);
    this.contextRefreshTimer = setTimeout(() => {
      this.contextRefreshTimer = null;
      this.accountId = this.normalize(localStorage.getItem("accountId")) || this.accountId;
      this.region = this.normalize(localStorage.getItem("regionId")) || this.region;
      this.loadRequests(true);
    }, 150);
  }

  async loadRequests(reset: boolean = false) {
    if (reset) { this.rows = []; this.nextKey = ""; this.hasMore = false; }
    const sequence = ++this.loadSequence;
    const accountId = this.accountId;
    const region = this.region;
    this.loading = true; this.error = "";
    this.notifier.loading(true);
    if (!accountId || !region) {
      if (sequence === this.loadSequence) {
        this.loading = false;
        this.notifier.loading(false);
        this.error = "Account and region are required.";
        this.refreshView();
      }
      return;
    }

    let path = `apis/eks_launch/api/eks/requests?aws_account_id=${encodeURIComponent(accountId)}&region=${encodeURIComponent(region)}&limit=${this.pageSize}`;
    if (!reset && this.nextKey) path += `&next_key=${encodeURIComponent(this.nextKey)}`;

    const prefixedUrl = `${this.apiBaseUrl}/${this.urlPrefix}/${path}`;
    const r = await this.get(prefixedUrl);
    if (sequence !== this.loadSequence || accountId !== this.accountId || region !== this.region) return;
    this.loading = false;
    this.notifier.loading(false);
    if (!r) {
      this.error = "Unable to load EKS requests. Please verify API base path/prefix.";
      this.refreshView();
      return;
    }
    const items = Array.isArray(r.items) ? r.items : [];
    this.rows = reset ? items : [...this.rows, ...items];
    this.nextKey = r.next_key || "";
    this.hasMore = !!this.nextKey;
    this.refreshView();
    this.loadRuntimeStatusesForEligibleRows(items);
  }

  private async loadRuntimeStatusesForEligibleRows(items: any[]) {
    const accountId = this.accountId;
    const region = this.region;
    const targets = (items || []).filter((row: any) => (row?.status || "").toUpperCase() === "CLUSTER_CREATED");
    await Promise.all(targets.map((row: any) => this.fetchClusterRuntimeStatus(row, accountId, region)));
  }

  private async fetchClusterRuntimeStatus(row: any, accountId: string, dashboardRegion: string) {
    const requestId = row?.request_id || "";
    const clusterName = (row?.cluster_name || "").trim();
    if (!requestId || !clusterName) return;
    this.statusLoadingByRequestId[requestId] = true;
    const account = accountId;
    const region = row?.region || dashboardRegion;
    const path = `apis/eks_launch/api/eks/cluster/status?aws_account_id=${encodeURIComponent(account)}&region=${encodeURIComponent(region)}&cluster_name=${encodeURIComponent(clusterName)}`;
    const prefixedUrl = `${this.apiBaseUrl}/${this.urlPrefix}/${path}`;
    const r = await this.get(prefixedUrl);
    if (accountId !== this.accountId || dashboardRegion !== this.region) return;
    const liveStatus = (r?.status || r?.cluster_status || "").toString().trim().toUpperCase();
    if (liveStatus) this.clusterRuntimeStatusByRequestId[requestId] = liveStatus;
    this.statusLoadingByRequestId[requestId] = false;
    this.refreshView();
  }

  displayStatus(row: any): string {
    const requestId = row?.request_id || "";
    if (this.clusterRuntimeStatusByRequestId[requestId]) return this.clusterRuntimeStatusByRequestId[requestId];
    return row?.status || "-";
  }

  canAddNodeGroup(row: any): boolean {
    return this.displayStatus(row) === "ACTIVE";
  }

  nodeGroupQueryParams(row: any) {
    const params: any = {
      aws_account_id: this.accountId,
      region: row?.region || this.region,
      cluster_request_id: row?.request_id,
    };
    if (this.preserveServiceCatalogHeader) {
      params.source = "service-catalog";
    }
    return params;
  }

  openRequestEksModal() {
    this.showRequestEksModal = true;
  }

  closeRequestEksModal() {
    this.showRequestEksModal = false;
  }
}
